import { createContext, type ReactNode, use, useEffect, useState } from "react";

export type Theme = "light" | "dark";

interface ThemeContextType {
	theme: Theme;
	setTheme: (theme: Theme) => void;
	toggleTheme: () => void;
}

const THEME_STORAGE_KEY = "theme";

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const getInitialTheme = (): Theme => {
	if (typeof window === "undefined") return "light";
	const stored = localStorage.getItem(THEME_STORAGE_KEY);
	return stored === "dark" ? "dark" : "light";
};

export function ThemeProvider({ children }: { children: ReactNode }) {
	const [theme, setThemeState] = useState<Theme>(getInitialTheme);

	useEffect(() => {
		if (typeof document === "undefined") return;

		const disableTransitions = document.createElement("style");
		disableTransitions.textContent =
			"*, *::before, *::after { transition: none !important; }";
		document.head.appendChild(disableTransitions);

		document.documentElement.setAttribute("data-theme", theme);

		const frame = requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				disableTransitions.remove();
			});
		});

		return () => {
			cancelAnimationFrame(frame);
			disableTransitions.remove();
		};
	}, [theme]);

	const setTheme = (next: Theme) => {
		setThemeState(next);
		if (typeof window !== "undefined") {
			localStorage.setItem(THEME_STORAGE_KEY, next);
		}
	};

	const toggleTheme = () => setTheme(theme === "dark" ? "light" : "dark");

	return (
		<ThemeContext value={{ theme, setTheme, toggleTheme }}>
			{children}
		</ThemeContext>
	);
}

export const useTheme = () => {
	const context = use(ThemeContext);
	if (context === undefined) {
		throw new Error("useTheme must be used within a ThemeProvider");
	}

	return context;
};
